import { Vue, Component, Watch } from 'vue-property-decorator';
import { Getter, State as StateClass } from 'vuex-class';
import { MetaInfo } from 'vue-meta';
import { collections } from '@/vue';
import { State } from '@/store/models';
import { Asset } from '@/store/models/asset';
import BannerLegal from '@/components/common/banner-legal/BannerLegal.vue';
import Navbar from '@/components/common/navbar/Navbar.vue';
import AccountUpgrade from '@/components/account/infobox/AccountUpgrade.vue';
import Footer from '@/components/common/footer/Footer.vue';
import ToolbarFilters from '@/components/properties/toolbar/filters/ToolbarFilters.vue';
import ToolbarSearch from '@/components/properties/toolbar/search/ToolbarSearch.vue';
import ToolbarPagination from '@/components/properties/toolbar/pagination/ToolbarPagination.vue';
import PropertiesGridList from '@/components/properties/grid-list/PropertiesGridList.vue';
import Support from '@/components/common/support/Support.vue';
import { premiumAssetTemplate } from '@/helpers/premium-assets/template';
import { metaTitles, metaDescriptions, metaURL, metaLocale, linkAlternate } from '@/helpers/meta';
import { titleTemplate } from '@/helpers/meta/title-template';
import { intercom } from '../../../whitelabel.config';

// Function that sorts first by shares available (if > 0 has prio), then by other rules
const sortWithAvaliableShares = (assets: Asset[], extraSorting?: (a: Asset, b: Asset) => number): Asset[] =>
  assets.sort((a, b): number => {
    if (a.sharesAvailable && !b.sharesAvailable) {
      return -1;
    }

    if (!a.sharesAvailable && b.sharesAvailable) {
      return 1;
    }

    if (!extraSorting) {
      return 0;
    }

    return extraSorting(a, b);
  });

@Component({
  components: {
    BannerLegal,
    Navbar,
    AccountUpgrade,
    Footer,
    ToolbarFilters,
    ToolbarSearch,
    ToolbarPagination,
    PropertiesGridList,
    Support,
  },
})
export default class Properties extends Vue {
  metaInfo(): MetaInfo {
    const { path } = this.$route;
    const { locale, availableLocales } = this.$i18n;
    return {
      title: titleTemplate(this.$t('meta.properties.title').toString()),
      link: [
        ...linkAlternate(path, availableLocales),
      ],
      meta: [
        ...metaTitles(this.$t('meta.properties.title').toString()),
        ...metaDescriptions(this.$t('meta.properties.description').toString()),
        ...metaURL(path),
        ...metaLocale(locale, availableLocales),
      ],
    };
  }

  intercom = intercom;

  promisedAssetsCollection = collections.assets;

  orderType: 'date' | 'search' = 'date';
  assetsOrder: 'DESC' | 'ASC' = 'DESC';
  searchText = '';
  filteredAssets: Asset[] = [];
  maxAssetsPerPage = 6;
  paginationPosition = 0;

  @StateClass user!: State['user'];
  @StateClass assets!: State['assets'];
  @StateClass misc!: State['misc'];
  @Getter isUserLoggedIn!: boolean;

  @Watch('assets', { immediate: true })
  onAssetsChange(newAssets: Asset[]): void {
    // Resetting filteredAssets
    this.filteredAssets = sortWithAvaliableShares([...newAssets]);

    if (!this.isUserLoggedIn) {
      this.filteredAssets = this.filteredAssets.map((asset): Asset => ({
        ...asset,
        // The id field is non-enumerable so we need to copy it explicitly since the spread operator won't copy it
        id: asset.id,
        // Use fake data for premium assets
        ...(asset.premium && premiumAssetTemplate(this.$i18n.locale)),
      }));
    }
  }

  mounted(): void {
    this.$watch(
      // Watching all those data inputs so we can do the same functionality in one same place
      // @ts-ignore
      (vm): any => [vm.orderType, vm.assetsOrder, vm.searchText].join(),
      (): void => {
        switch (this.orderType) {
          case 'date': {
            this.filteredAssets = sortWithAvaliableShares(
              this.filteredAssets,
              (a, b): number => this.assetsOrder === 'ASC' ? a.createdDateTime.seconds - b.createdDateTime.seconds : b.createdDateTime.seconds - a.createdDateTime.seconds,
            );
            break;
          }
          case 'search': {
            if (!this.searchText) {
              this.filteredAssets = [...this.assets];
            } else {
              const loweredCaseSearchText = this.searchText.toLocaleLowerCase();
              this.filteredAssets = [
                ...this.assets.filter((asset): boolean => [asset.name, asset.city, (this.$options.filters as any).transformDate(asset.startDateTime)]
                  .map((field): string => field.toLocaleLowerCase())
                  .some((field): boolean => field.includes(loweredCaseSearchText)))];
            }

            // Descending order by default, so we order only when ascending is selected
            if (this.assetsOrder === 'ASC') {
              this.orderType = 'date';
            }
            break;
          }
          default:
            break;
        }
      },
    );
  }

  get paginatedAssets(): Asset[] {
    return this.paginate(this.filteredAssets);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredAssets.length / this.maxAssetsPerPage);
  }

  handlePagination(page: number): void {
    this.paginationPosition = (page - 1) * this.maxAssetsPerPage;
  }

  paginate(assetsArray: Asset[]): Asset[] {
    return assetsArray.slice(this.paginationPosition, this.paginationPosition + this.maxAssetsPerPage);
  }

  handleOrder(assetsOrder: 'DESC' | 'ASC'): void {
    this.assetsOrder = assetsOrder;
    this.orderType = 'date';
    this.$gtm.trackEvent({
      event: 'properties_order',
      email: this.user?.email,
      type: this.orderType,
      order: assetsOrder,
    });
  }

  handleSearch(searchText: ''): void {
    this.searchText = searchText;
    this.orderType = 'search';
    this.$gtm.trackEvent({
      event: 'properties_search',
      email: this.user?.email,
      type: searchText,
    });
  }
}
